<template>
  <LMap
    ref="map"
    v-if="height"
    :height="mapHeight"
    :zoom="map.zoom"
    :center="map.center"
    :options="map.opts"
    :class="{ dark }"
    @click="clickMap"
    @movestart="movestart"
    @moveend="moveend"
  >
    <!-- tiles -->
    <LMapboxGL
      :url="tiles"
      attribution="<a href='https://www.maptiler.com/copyright/' target='_blank' rel='noopener'>© MapTiler</a> <a href='https://www.openstreetmap.org/copyright' target='_blank' rel='noopener'>© OSM</a>"
    />

    <!-- markers -->
    <LMarkerClusterGroup
      v-if="full"
      :options="{ showCoverageOnHover: false }"
    >
      <LMarker
        v-for="m in markers"
        :key="'m' + m._id"
        :latlng="[m.coords[1], m.coords[0]]"
        @click="clickMarker(m)"
      >
        <LAwesomeMarker :options="markerOpts(m)" />
      </LMarker>
    </LMarkerClusterGroup>

    <LFeatureGroup
      ref="circlesGroup"
      v-else
    >
      <LMarker
        v-if="highlight"
        :latlng="[highlight.coords[1], highlight.coords[0]]"
        @click="clickSpecialMarker(highlight)"
      >
        <LAwesomeMarker :options="markerOpts(highlight)" />
      </LMarker>
      <LCircleMarker
        v-for="c in circles"
        :key="'c' + c._id"
        :latlng="[c.coords[1], c.coords[0]]"
        @click="clickCircle(c)"
        :options="{
          radius: 12,
          color: circleColor(c),
          weight: 1,
          fillOpacity: .5
        }"
      />
    </LFeatureGroup>

    <LFeatureGroup
      ref="sepcialMarkers"
      v-if="full"
    >
      <LMarker
        v-for="m in specialMarkers"
        :key="'spec-' + m._id"
        :latlng="[m.coords[1], m.coords[0]]"
        @click="clickSpecialMarker(m)"
      >
        <LAwesomeMarker :options="markerOpts(m)" />
      </LMarker>
    </LFeatureGroup>

    <!-- <LGeoJSON
      v-if="area"
      :geojson="area" /> -->
    <LFeatureGroup
      ref="routeGroup"
      v-if="routePolyline && routePolyline.length > 0"
    >
      <LPolyline
        ref="path"
        :latlngs="routePolyline"
        :options="{
          weight: 4,
          color: '#d53e2a'
        }"
        @click="clickRoute"
      />
    </LFeatureGroup>

    <!-- user location -->
    <LCircleMarker
      v-if="geoPos && follow && geoPrecision > 20"
      :latlng="geoPos"
      :options="{
        color: '#29abe2',
        radius: geoPrecision,
        weight: 0,
        fillOpacity: .2
      }"
    />
    <LCircleMarker
      v-if="geoPos"
      @click="clickPos"
      :latlng="geoPos"
      :options="{
        color: '#29abe2',
        radius: 8,
        weight: 0,
        fillOpacity: geoActive ? 1 : .2
      }"
    />

  </LMap>
</template>

<script>
import debounce from 'lodash/debounce'
import * as turf from '@turf/turf'
import { mapGetters, mapActions } from 'vuex'
import { tilesStyles } from '@/services/maptiler'
import { markers } from '@/services/atlastories'

export default {
  name: 'LeafletMap',
  props: {
    height: {
      required: true
    },
    full: {
      type: Boolean
    }
  },
  data () {
    return {
      map: {
        center: [48, 2],
        zoom: 3,
        opts: {
          zoomControl: false,
          keyboard: false,
          maxZoom: 20,
          minZoom: 6,
          zoomSnap: 1
        }
      },
      loading: false,
      area: null,
      mapHeight: this.height
    }
  },
  computed: {
    ...mapGetters({
      geoPos: 'global/geoPos',
      geoActive: 'global/geoActive',
      geoPrecision: 'global/geoPrecision',
      markers: 'global/markers',
      swiper: 'global/swiper',
      highlight: 'global/highlight',
      routePolyline: 'global/routePolyline',
      flyTo: 'global/flyTo',
      markerSpeech: 'speech/markerSpeech',
      routeMarker: 'global/routeMarker'
    }),
    tiles () { return tilesStyles(this.setting('tiles')) },
    follow () { return this.setting('follow') },
    circles () {
      return this.highlight ? this.swiper.filter(m => m._id !== this.highlight._id) : this.swiper
    },
    specialMarkers () {
      let ms = []
      if (this.highlight) ms.push(this.highlight)
      if (this.markerSpeech && !ms.map(m => m._id).includes(this.markerSpeech._id)) ms.push(this.markerSpeech)
      if (this.routeMarker && !ms.map(m => m._id).includes(this.routeMarker._id)) ms.push(this.routeMarker)
      return ms
    }
  },
  watch: {
    // when components marker's loading, set store value
    loading (b) {
      this.setMarkersLoading(b)
    },
    // when map height change, trigger invalidateSize
    // for leaflet to manage the change of map size
    height (val) {
      if (val && !this.full) {
        setTimeout(() => {
          this.mapHeight = val
          this.$nextTick(() => {
            this.invalidateSize()
          })
        }, 50)
      } else if (val) {
        this.mapHeight = val
        this.$nextTick(() => {
          this.invalidateSize()
        })
      }
    },
    // when map is full height, then it s time to remove the swipper and highlight
    full (val, old) {
      // when map is not full, swiper opens
      // when no highlight define carousel
      if (!val && !this.highlight) {
        this.clickMarker()
      }
      // if map is full, swiper is closed
      // remove highlight
      if (val) {
        this.setHighlight(null)
      }
    },
    // when lang change, get new markers
    lang (val, old) {
      if (val && val !== old) {
        this.getMarkers()
      }
    },
    routePolyline (data) {
      console.log('routePolyline -> data', data)
      if (data) {
        this.$nextTick(() => {
          this.fitRoute()
        })
      }
    },
    flyTo (val, old) {
      if (val && !old) this.zoomTo(val)
    },
    geoPos (val) {
      if (val && this.follow) this.zoomTo(val)
    }
  },
  methods: {
    ...mapActions({
      setSetting: 'global/setSetting',
      setMarkers: 'global/setMarkers',
      setMarkersLoading: 'global/setMarkersLoading',
      setSwiper: 'global/setSwiper',
      setFlyTo: 'global/setFlyTo',
      setHighlight: 'global/setHighlight',
      startMarkerPlaylist: 'speech/startMarkerPlaylist',
      stopCurrentPlaylist: 'speech/stopCurrentPlaylist'
    }),

    // map movements callbacks

    movestart () {
      // reset flyto if exists
      if (this.flyTo) this.setFlyTo(null)
    },
    moveend: debounce(function (e) {
      const map = e.target
      const c = map.getCenter()
      const center = [c.lat, c.lng]
      const zoom = map.getZoom()
      this.map.zoom = zoom
      this.setSetting({ center, zoom })
      this.$emit('moveend', e)
      // check if map center is within
      // the area of existing markers
      const withinArea = this.area
        ? turf.booleanPointInPolygon(turf.point([c.lng, c.lat]), this.area)
        : false
      if (!this.loading && !withinArea) this.getMarkers(map)
    }, 800),

    // I/O

    getMarkers () {
      if (this.$refs.map) {
        const map = this.$refs.map.layer
        this.loading = true
        this.area = null
        const bounds = map.getBounds()
        // convert to turf bbox
        const bbox = [
          bounds._southWest.lng,
          bounds._southWest.lat,
          bounds._northEast.lng,
          bounds._northEast.lat
        ]
        markers(bbox).then(res => {
          // get the area
          const points = res.length
            ? res.map(p => turf.point(p.coords))
            : []
          const area = points.length
            ? turf.buffer(
              turf.convex(turf.featureCollection(points)),
              '0.15', { units: 'kilometers' }) // add 150m around
            : null
          this.area = area
          this.setMarkers(res)
          // this.$db.markers.bulkAdd(res).then(lastKey => {
          //   console.log(lastKey)
          // })
          this.loading = false
        }).catch(err => {
          console.log(err)
          this.loading = false
        })
      }
    },

    // User interactions

    clickMap (e) {
      if (e.originalEvent.target.classList.contains('fa')) {
        // marker clicked
      } else if (e.originalEvent.target.nodeName === 'path') {
        // circle clicked
      }
      if (e.originalEvent.target.classList.contains('leaflet-gl-layer')) {
        this.$emit('closeSheet')
      }
    },
    // main function to define swiper content
    clickMarker (marker) {
      if (this.markers.length) {
        // swiper
        const bounds = this.$refs.map.layer.getBounds()
        const bbox = [bounds._southWest.lng, bounds._southWest.lat, bounds._northEast.lng, bounds._northEast.lat]
        const bboxPolygon = this.$turf.bboxPolygon(bbox)
        let swiper = []
        // make sure items are in viewport
        for (const m of this.markers) {
          const point = this.$turf.point(m.coords)
          if (this.$turf.booleanPointInPolygon(point, bboxPolygon)) {
            swiper.push(m)
          }
        }
        swiper.sort((a, b) => b.score - a.score) // sort by score
        // make sure highlighted or default is the first
        if (marker) this.setHighlight(marker)
        else if (this.markerSpeech) this.setHighlight(this.markerSpeech)
        else if (swiper.length) this.setHighlight(swiper[0])
        // remove and reinject at begining the highlight
        if (this.highlight) {
          swiper = swiper.filter(m => m._id !== this.highlight._id)
          swiper.unshift(this.highlight)
        }
        // remove the speech if present and inject it at first
        if (this.markerSpeech) {
          swiper = swiper.filter(m => m._id !== this.markerSpeech._id)
          swiper.unshift(this.markerSpeech)
        }
        // cut down to the main stuff
        swiper = swiper.slice(0, 10) // get the real interesting stuff
        // set swiper
        if (swiper.length) {
          this.setSwiper(swiper)
          this.$emit('openSheet')
        }
        // find the swiper's bbox
        if (this.circles.length > 1) {
          const points = this.markerSpeech ? this.circles.filter(m => m._id !== this.markerSpeech._id) : this.circles
          if (points.length > 1) {
            const line = this.$turf.lineString(points.map(m => m.coords))
            const circlesBbox = this.$turf.bbox(line)
            this.$nextTick(() => {
              setTimeout(() => {
                this.fitBbox(circlesBbox)
              }, 20)
            })
          } else {
            setTimeout(() => {
              this.zoomTo([points[0].coords[1], points[0].coords[0]], 16)
            }, 20)
          }
        } else {
          // only one point, center it
          if (marker) {
            setTimeout(() => {
              this.zoomTo([marker.coords[1], marker.coords[0]], 16)
            }, 20)
          }
        }
      }
    },
    clickSpecialMarker (m) {
      this.clickMarker(m)
    },
    clickCircle (marker) {
      this.setHighlight(marker)
    },
    clickPos () {
      this.zoomTo(this.geoPos, 16)
    },
    clickRoute () {
      this.$f7.views.main.router.navigate('/route')
    },
    touchstart (e) {
      if (e.originalEvent.target.classList.contains('leaflet-gl-layer') && this.highlight) {
        // e.originalEvent.preventDefault()
        this.$emit('closeSheet')
      }
      // remove follow mode if map is touched
      if (this.follow) this.setSetting({ follow: false })
    },

    // utility to parents to access leaflet instance and leaflet resize method

    layer () {
      return this.$refs.map.layer // expose map object for parent
    },
    invalidateSize () {
      this.$refs.map.layer.invalidateSize(true)
    },

    // ui dynamic style

    markerOpts (m) {
      let icon = 'circle'
      let markerColor = 'orange'
      if (this.markerSpeech && m._id === this.markerSpeech._id) {
        icon = 'play-circle'
        markerColor = 'blue'
      }
      if (this.routeMarker && m._id === this.routeMarker._id) {
        icon = 'flag'
        markerColor = 'red'
      }
      if (this.markerSpeech && this.routeMarker && m._id === this.markerSpeech._id && m._id === this.routeMarker._id) {
        icon = 'play-circle'
      }
      return {
        icon,
        markerColor,
        prefix: 'fa'
      }
    },
    circleColor (m) {
      if (this.routeMarker && this.routeMarker._id === m._id) return '#d53e2a'
      if (this.markerSpeech && this.markerSpeech._id === m._id) return '#29abe2'
      return '#fbb03b'
    },

    // map movements

    zoomTo (pos, zoom = 16) {
      if (this.$refs.map) this.$refs.map.layer.setView(pos, zoom)
    },
    fitRoute () {
      if (this.$refs.path && this.$refs.map) {
        const bounds = this.$refs.path.layer.getBounds()
        this.$refs.map.layer.fitBounds(bounds)
      }
    },
    fitBbox (bbox) {
      const bounds = [
        [bbox[1], bbox[0]],
        [bbox[3], bbox[2]]
      ]
      if (this.$refs.map) this.$refs.map.layer.fitBounds(bounds, { padding: [60, 60] })
    },
    fitMarkers () {
      let bbox = []
      for (const m of this.markers) {
        bbox.push(m.coords)
      }
      bbox = this.$turf.lineString(bbox)
      bbox = this.$turf.bbox(bbox)
      this.$nextTick(() => {
        this.fitBbox(bbox)
      })
    }
  },
  created () {
    this.map.zoom = this.setting('zoom')
    this.map.center = this.follow && this.geoPos ? this.geoPos : this.setting('center')
  },
  mounted () {
    this.getMarkers()
    this.$refs.map.layer.on('mousedown touchstart', this.touchstart)
    // this.$refs.map.$el.addEventListener('wheel', this.wheel)
  }
}
</script>

<style lang="scss">
.dark.leaflet-container {
  background-color: black;
}
</style>
